import React, { useState, useEffect } from 'react';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Tooltip, Legend } from 'chart.js';
import { Bar } from 'react-chartjs-2';
import { ContentCenter, Form } from '../../../components/commons';
import { useTheme } from 'styled-components';
import { api } from '../../../services/api';
import { DashStyles, ChartStyle, ChartTitle, Container } from '../Home/styles';
import { useNavigate } from 'react-router-dom';
import useIsMobile from '../../../hooks/useIsMobile';
import { useForm } from 'react-hook-form';
import SelectInput from '../../../components/Input/SelectInput';
import Input from '../../../components/Input';
import Button from '../../../components/Button';
import { FilterStyles } from '../Home/styles';
import { LoaderComponent } from '../../../components/Table/styles';
import Table from '../../../components/Table';
import useDocuments from '../../../services/documents.service';
import useDocumentTypes from '../../../services/documentTypes.service';
import { format } from 'date-fns';
import {
  reportsService,
  IArchiveOccupancy,
  IPhysicalMovementsReport,
  ICpadOfficialReport,
  ILgpdAuditReport,
} from '../../../services/reports.service';

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend);

// Subcomponente de Pastas
const FoldersLevel = ({ control, level, parentId }: { control: any; level: number; parentId?: string }) => {
  const [search, setSearch] = useState<string>();
  const { isLoading, data } = useDocuments(1, 18, search, undefined, parentId ? [parentId] : [], {
    isFolder: true,
  });

  return (
    <SelectInput
      control={control}
      onChangeText={(s) => setSearch(s)}
      options={data?.data.map((document) => ({
        label: document?.name,
        value: document?._id,
      }))}
      isLoading={isLoading}
      placeholder={level === 0 ? 'Selecionar Pastas' : 'Selecionar Subpastas'}
      isMulti
      gray
      name={`folders[${level}]`}
    />
  );
};

const CascadingFolders = ({ control, watch }: { control: any; watch: any }) => {
  const formValues = watch();
  const foldersLevels = Object.values(formValues?.folders || {});

  return (
    <>
      <FoldersLevel control={control} level={0} />
      {foldersLevels.map((selectedOptions: any, index: number) => {
        if (selectedOptions && selectedOptions.length === 1) {
          return <FoldersLevel key={index + 1} control={control} level={index + 1} parentId={selectedOptions[0].value} />;
        }
        return null;
      })}
    </>
  );
};

const DocumentTypesList = ({ control }: { control: any }) => {
  const [search, setSearch] = useState<string>();
  const { isLoading, data } = useDocumentTypes(1, 18, search, '');

  return (
    <SelectInput
      control={control}
      onChangeText={(s) => setSearch(s)}
      options={data?.data.map((docType: any) => ({
        label: docType?.name,
        value: docType?._id,
      }))}
      isLoading={isLoading}
      placeholder="Selecionar Tipo de Documento"
      isMulti
      gray
      name={'documentTypes'}
    />
  );
};

const GeneralFilters = ({ handleGetStatistics }: { handleGetStatistics: (date: any, documentTypes: any, folders: any) => void }) => {
  const { control, reset, watch } = useForm<any>();
  const formValues = watch();
  const dateStart: any = formValues?.dateStart;
  const dateEnd: any = formValues?.dateEnd;
  const documentTypes: any = formValues?.documentTypes;
  const folders: any = formValues?.folders;

  useEffect(() => {
    let validFolders: any[] = [];
    const foldersLevels = Object.values(folders || {});
    if (foldersLevels[0]) validFolders = [...(foldersLevels[0] as any[])];
    for (let i = 0; i < foldersLevels.length; i++) {
      const currentLevel = foldersLevels[i] as any[];
      const nextLevel = foldersLevels[i + 1] as any[];
      if (currentLevel && currentLevel.length === 1 && nextLevel && nextLevel.length > 0) {
        validFolders = [...nextLevel];
      } else if (currentLevel && currentLevel.length !== 1) {
        break;
      }
    }

    handleGetStatistics({ startDate: dateStart, endDate: dateEnd }, documentTypes, validFolders);
  }, [dateStart, dateEnd, JSON.stringify(documentTypes), JSON.stringify(folders)]);

  return (
    <FilterStyles.Content>
      <FilterStyles.Title>Filtros</FilterStyles.Title>
      <Form>
        <div style={{ display: 'flex', gap: 10 }}>
          <Input control={control} name={'dateStart'} gray type="date" placeholder="Data Início" />
          <Input control={control} name={'dateEnd'} gray type="date" placeholder="Data Fim" />
        </div>
        <DocumentTypesList control={control} />
        <CascadingFolders control={control} watch={watch} />
        <ContentCenter>
          <Button label="Limpar" onClick={() => reset()} color="primary" variantType="small" />
        </ContentCenter>
      </Form>
    </FilterStyles.Content>
  );
};

const GeneralChart = ({ title, total = 0, filter = 0, warning = false }: { title: string; total?: number; filter?: number; warning?: boolean }) => {
  const theme: any = useTheme();
  const data = {
    labels: ['Total', 'Filtros'],
    datasets: [
      {
        label: 'Quantidade',
        data: [total, filter],
        backgroundColor: [theme.colors.primary, theme.colors.secondary],
        borderColor: [theme.colors.primary, theme.colors.secondary],
        borderWidth: 1,
      },
    ],
  };

  const options = {
    responsive: true,
    plugins: {
      legend: {
        display: false,
      },
    },
  };

  return (
    <ChartStyle.Content>
      <div style={{ padding: 10, width: '100%', maxWidth: 300, margin: 'auto', textAlign: 'center' }}>
        <ChartTitle>
          {title}
          {warning ? '*' : ''}
        </ChartTitle>
        <div style={{ marginTop: 10 }}>
          <Bar data={data} options={options} />
        </div>
        <div style={{ marginTop: 15, display: 'flex', justifyContent: 'space-between', color: theme.colors.text }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
            <div style={{ width: 12, height: 12, backgroundColor: theme.colors.primary, borderRadius: '50%' }} />
            <span>
              Total: <strong>{total}</strong>
            </span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
            <div style={{ width: 12, height: 12, backgroundColor: theme.colors.secondary, borderRadius: '50%' }} />
            <span>
              Filtros: <strong>{filter}</strong>
            </span>
          </div>
        </div>
      </div>
    </ChartStyle.Content>
  );
};

// Componente Principal
const Reports: React.FC = () => {
  const mobile = useIsMobile();
  const nav = useNavigate();
  const theme: any = useTheme();

  // Abas
  const [activeTab, setActiveTab] = useState<'general' | 'cpad' | 'occupancy' | 'movements' | 'lgpd'>('general');

  // Dados Geral
  const [statistics, setStatistics] = useState<any>();
  const [isLoading, setIsLoading] = useState<boolean>(false);

  // Dados CPAD
  const [cpadReport, setCpadReport] = useState<ICpadOfficialReport>();
  const [selectedTermId, setSelectedTermId] = useState<string>('');
  const [loadingCpad, setLoadingCpad] = useState<boolean>(false);

  // Dados Ocupação Arquivo Físico
  const [occupancyReport, setOccupancyReport] = useState<IArchiveOccupancy>();
  const [loadingOccupancy, setLoadingOccupancy] = useState<boolean>(false);

  // Dados Movimentações
  const [movementsReport, setMovementsReport] = useState<IPhysicalMovementsReport>();
  const [movementFilterStatus, setMovementFilterStatus] = useState<string>('all');
  const [loadingMovements, setLoadingMovements] = useState<boolean>(false);

  // Dados LGPD
  const [lgpdReport, setLgpdReport] = useState<ILgpdAuditReport>();
  const [loadingLgpd, setLoadingLgpd] = useState<boolean>(false);

  // Carregar dados gerais
  const handleGetStatistics = async (date: any, documentTypes: any, folders: any) => {
    setIsLoading(true);
    try {
      const { data: response } = await api.get('/dashboard/statistics', {
        params: {
          dateStart: date?.startDate,
          dateEnd: date?.endDate,
          documentTypes: JSON.stringify(documentTypes?.map((a: any) => a.value) || []),
          folders: JSON.stringify(folders?.map((f: any) => f.value) || []),
        },
      });
      setStatistics(response);
    } catch (error) {
      nav('/documents');
    } finally {
      setIsLoading(false);
    }
  };

  // Carregar CPAD
  const loadCpadReport = async (termId?: string) => {
    setLoadingCpad(true);
    try {
      const res = await reportsService.getCpadOfficial(termId);
      setCpadReport(res);
      if (res.terms && res.terms.length > 0 && !selectedTermId) {
        setSelectedTermId(res.terms[0].id);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingCpad(false);
    }
  };

  // Carregar Ocupação
  const loadOccupancyReport = async () => {
    setLoadingOccupancy(true);
    try {
      const res = await reportsService.getArchiveOccupancy();
      setOccupancyReport(res);
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingOccupancy(false);
    }
  };

  // Carregar Movimentações
  const loadMovementsReport = async (status: string) => {
    setLoadingMovements(true);
    try {
      const res = await reportsService.getPhysicalMovements({ status });
      setMovementsReport(res);
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingMovements(false);
    }
  };

  // Carregar LGPD
  const loadLgpdReport = async () => {
    setLoadingLgpd(true);
    try {
      const res = await reportsService.getLgpdAudit();
      setLgpdReport(res);
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingLgpd(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'cpad') loadCpadReport(selectedTermId || undefined);
    if (activeTab === 'occupancy') loadOccupancyReport();
    if (activeTab === 'movements') loadMovementsReport(movementFilterStatus);
    if (activeTab === 'lgpd') loadLgpdReport();
  }, [activeTab]);

  // Função para exportação CSV de tabela genérica
  const exportCsv = (filename: string, headers: string[], rows: (string | number)[][]) => {
    const csvContent =
      'data:text/csv;charset=utf-8,' +
      [headers.join(','), ...rows.map((e) => e.map((val) => `"${val}"`).join(','))].join('\n');
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    link.setAttribute('download', `${filename}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const selectedCpadTerm = cpadReport?.terms?.find((t) => t.id === selectedTermId) || cpadReport?.terms?.[0];

  return (
    <Container>
      {activeTab === 'general' && (
        <DashStyles.RightPanel hidden={!mobile}>
          <GeneralFilters handleGetStatistics={handleGetStatistics} />
        </DashStyles.RightPanel>
      )}

      <DashStyles.LeftPanel style={{ flex: 1, padding: '0 24px 40px 24px' }}>
        {/* Cabeçalho */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', margin: '20px 0 15px 0', flexWrap: 'wrap', gap: 12 }}>
          <div>
            <h1 style={{ color: theme.colors.title, margin: 0, fontSize: 24 }}>Relatórios & Estatísticas Oficiais</h1>
            <p style={{ margin: '4px 0 0 0', fontSize: 13, color: '#64748b' }}>
              Gestão Documental, Comissão de Avaliação (CPAD), Arquivo Físico e Auditoria LGPD
            </p>
          </div>
        </div>

        {/* Abas de Navegação */}
        <div
          style={{
            display: 'flex',
            gap: 8,
            borderBottom: '2px solid #e2e8f0',
            marginBottom: 20,
            overflowX: 'auto',
            paddingBottom: 4,
          }}
        >
          {[
            { id: 'general', label: '📊 Estatísticas Gerais', desc: 'Volumetria & Páginas' },
            { id: 'cpad', label: '🏛️ CPAD & Diário Oficial', desc: 'Termos de Eliminação e Guarda' },
            { id: 'occupancy', label: '📦 Ocupação do Arquivo Físico', desc: 'Galpões & Caixas' },
            { id: 'movements', label: '🔄 Custódia & Empréstimos', desc: 'Rastreabilidade Física' },
            { id: 'lgpd', label: '🛡️ Auditoria & LGPD', desc: 'Assinaturas & Trilha de Auditoria' },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as any)}
              style={{
                padding: '10px 16px',
                border: 'none',
                background: activeTab === tab.id ? theme.colors.primary : '#f8fafc',
                color: activeTab === tab.id ? '#fff' : '#475569',
                borderRadius: '8px 8px 0 0',
                fontWeight: activeTab === tab.id ? 700 : 500,
                fontSize: 13,
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                gap: 2,
                whiteSpace: 'nowrap',
              }}
            >
              <span>{tab.label}</span>
              <span style={{ fontSize: 10, opacity: 0.85 }}>{tab.desc}</span>
            </button>
          ))}
        </div>

        {/* ABA 1: Estatísticas Gerais */}
        {activeTab === 'general' && (
          <>
            {isLoading ? (
              <div style={{ display: 'flex', flex: 1, height: '300px', alignItems: 'center', justifyContent: 'center' }}>
                <LoaderComponent />
              </div>
            ) : (
              <>
                <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap' }}>
                  <GeneralChart title="Documentos" total={statistics?.documentsTotal} filter={statistics?.documentsFilter} />
                  <GeneralChart
                    title="Páginas"
                    total={statistics?.pagesTotal}
                    filter={statistics?.pagesFilter}
                    warning={statistics?.missingPagesFilterCount > 0}
                  />
                </div>

                <div style={{ marginTop: 24, width: '100%', overflowX: 'auto' }}>
                  <h2 style={{ color: theme.colors.title, margin: '15px 0', fontSize: 18 }}>Resumo por Tipo de Documento</h2>
                  {statistics?.typeBreakdown ? (
                    <Table
                      columns={[
                        { header: 'Tipo de Documento', accessorKey: 'name' },
                        { header: 'Documentos', accessorKey: 'documentsCount' },
                        { header: 'Páginas', accessorKey: 'pagesCount' },
                      ]}
                      data={statistics.typeBreakdown}
                      isLoading={isLoading}
                    />
                  ) : (
                    <div style={{ padding: 20, color: theme.colors.placeholder, textAlign: 'center', backgroundColor: '#f9f9f9', borderRadius: 8 }}>
                      Selecione algum filtro (pasta, data ou tipo) no painel lateral para detalhar os tipos.
                    </div>
                  )}
                </div>
              </>
            )}
          </>
        )}

        {/* ABA 2: CPAD & Diário Oficial */}
        {activeTab === 'cpad' && (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 10 }}>
              <div>
                <span style={{ fontSize: 12, fontWeight: 600, color: '#64748b', display: 'block', marginBottom: 4 }}>
                  SELECIONE O TERMO HOMOLOGADO PELA COMISSÃO:
                </span>
                <select
                  value={selectedTermId}
                  onChange={(e) => {
                    setSelectedTermId(e.target.value);
                    loadCpadReport(e.target.value);
                  }}
                  style={{
                    padding: '8px 12px',
                    borderRadius: 6,
                    border: '1px solid #cbd5e1',
                    fontSize: 13,
                    background: '#fff',
                    minWidth: 320,
                  }}
                >
                  {cpadReport?.terms?.map((term) => (
                    <option key={term.id} value={term.id}>
                      {term.termNumber} — {term.type === 'eliminacao' ? 'Termo de Eliminação (TED)' : 'Termo de Guarda Permanente (TGP)'}
                    </option>
                  ))}
                </select>
              </div>

              {selectedCpadTerm && (
                <div style={{ display: 'flex', gap: 10 }}>
                  <button
                    onClick={() => window.print()}
                    style={{
                      padding: '9px 16px',
                      borderRadius: 6,
                      background: theme.colors.primary,
                      color: 'white',
                      border: 'none',
                      fontWeight: 600,
                      fontSize: 13,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 6,
                    }}
                  >
                    🖨️ Imprimir Edital / PDF Oficial
                  </button>
                  <button
                    onClick={() => {
                      exportCsv(
                        `Relatorio_CPAD_${selectedCpadTerm.termNumber}`,
                        ['Número do Termo', 'Documento', 'Código CONARQ', 'Tipo Documental', 'Data de Produção', 'Base Legal'],
                        selectedCpadTerm.documents.map((d) => [
                          selectedCpadTerm.termNumber,
                          d.name,
                          d.classificationCode,
                          d.documentTypeName,
                          d.documentDate ? format(new Date(d.documentDate), 'dd/MM/yyyy') : '-',
                          d.legalBasis,
                        ])
                      );
                    }}
                    style={{
                      padding: '9px 16px',
                      borderRadius: 6,
                      background: '#16a34a',
                      color: 'white',
                      border: 'none',
                      fontWeight: 600,
                      fontSize: 13,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 6,
                    }}
                  >
                    📊 Exportar Planilha (CSV)
                  </button>
                </div>
              )}
            </div>

            {loadingCpad ? (
              <LoaderComponent />
            ) : selectedCpadTerm ? (
              <div
                style={{
                  background: '#ffffff',
                  border: '1px solid #cbd5e1',
                  borderRadius: 8,
                  padding: 24,
                  boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                }}
              >
                {/* Cabeçalho do Termo Oficial */}
                <div style={{ textAlign: 'center', borderBottom: '2px solid #0f172a', paddingBottom: 16, marginBottom: 20 }}>
                  <span style={{ fontSize: 13, fontWeight: 800, color: '#334155', letterSpacing: '0.08em' }}>
                    REPÚBLICA FEDERATIVA DO BRASIL
                  </span>
                  <h2 style={{ margin: '6px 0', fontSize: 18, color: '#0f172a' }}>
                    {selectedCpadTerm.title.toUpperCase()}
                  </h2>
                  <span style={{ fontSize: 14, fontWeight: 700, color: theme.colors.primary }}>
                    REGISTRO Nº {selectedCpadTerm.termNumber}
                  </span>
                </div>

                {/* Metadados e Comissão */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 20, fontSize: 13 }}>
                  <div style={{ background: '#f8fafc', padding: 12, borderRadius: 6, border: '1px solid #e2e8f0' }}>
                    <strong>Comissão Permanente de Avaliação (CPAD):</strong>
                    <div style={{ marginTop: 6 }}>
                      <div>• Presidente: <strong>{selectedCpadTerm.committeePresident}</strong></div>
                      {selectedCpadTerm.evaluators.map((ev, i) => (
                        <div key={i}>• Membro Avaliador: {ev}</div>
                      ))}
                    </div>
                  </div>

                  <div style={{ background: '#f8fafc', padding: 12, borderRadius: 6, border: '1px solid #e2e8f0' }}>
                    <div><strong>Data de Homologação:</strong> {format(new Date(selectedCpadTerm.date), 'dd/MM/yyyy HH:mm')}</div>
                    <div style={{ marginTop: 4 }}><strong>Base Normativa / CONARQ:</strong> {selectedCpadTerm.legalBasis}</div>
                    <div style={{ marginTop: 4 }}><strong>Total de Itens:</strong> {selectedCpadTerm.totalDocuments} documento(s)</div>
                  </div>
                </div>

                <div style={{ marginBottom: 20, padding: 12, background: '#fffbeb', border: '1px solid #fef3c7', borderRadius: 6, fontSize: 12, color: '#92400e' }}>
                  <strong>Justificativa Circunstanciada da Comissão:</strong>
                  <p style={{ margin: '4px 0 0 0' }}>{selectedCpadTerm.justification}</p>
                </div>

                {/* Tabela de Documentos Avaliados */}
                <h3 style={{ fontSize: 14, color: '#0f172a', marginBottom: 10 }}>
                  Relação Nominal dos Documentos Avaliados para Diário Oficial
                </h3>
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                    <thead>
                      <tr style={{ background: '#f1f5f9', borderBottom: '2px solid #cbd5e1' }}>
                        <th style={{ padding: '8px 12px', textAlign: 'left' }}>Item</th>
                        <th style={{ padding: '8px 12px', textAlign: 'left' }}>Código CONARQ</th>
                        <th style={{ padding: '8px 12px', textAlign: 'left' }}>Nome do Arquivo / Registro</th>
                        <th style={{ padding: '8px 12px', textAlign: 'left' }}>Tipologia</th>
                        <th style={{ padding: '8px 12px', textAlign: 'left' }}>Data de Produção</th>
                        <th style={{ padding: '8px 12px', textAlign: 'left' }}>Base Legal</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedCpadTerm.documents.map((doc, idx) => (
                        <tr key={doc.id} style={{ borderBottom: '1px solid #e2e8f0' }}>
                          <td style={{ padding: '8px 12px' }}>{idx + 1}</td>
                          <td style={{ padding: '8px 12px', fontWeight: 600 }}>{doc.classificationCode}</td>
                          <td style={{ padding: '8px 12px' }}>{doc.name}</td>
                          <td style={{ padding: '8px 12px' }}>{doc.documentTypeName}</td>
                          <td style={{ padding: '8px 12px' }}>
                            {doc.documentDate ? format(new Date(doc.documentDate), 'dd/MM/yyyy') : '-'}
                          </td>
                          <td style={{ padding: '8px 12px', color: '#64748b' }}>{doc.legalBasis}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : (
              <div style={{ padding: 30, textAlign: 'center', background: '#f8fafc', borderRadius: 8, color: '#64748b' }}>
                Nenhum termo da CPAD registrado até o momento. Acesse o menu <strong>Avaliação & TTD (CPAD)</strong> para homologar avaliações.
              </div>
            )}
          </div>
        )}

        {/* ABA 3: Ocupação do Arquivo Físico */}
        {activeTab === 'occupancy' && (
          <div>
            {loadingOccupancy ? (
              <LoaderComponent />
            ) : occupancyReport ? (
              <div>
                {/* Cards de Métricas de Ocupação */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16, marginBottom: 20 }}>
                  <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 8, padding: 16 }}>
                    <span style={{ fontSize: 11, fontWeight: 700, color: '#1e40af' }}>TAXA DE OCUPAÇÃO GERAL</span>
                    <h2 style={{ margin: '6px 0 0 0', fontSize: 26, color: '#1e3a8a' }}>{occupancyReport.overallOccupancyRate}%</h2>
                  </div>
                  <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 8, padding: 16 }}>
                    <span style={{ fontSize: 11, fontWeight: 700, color: '#166534' }}>DOCUMENTOS CUSTODIADOS</span>
                    <h2 style={{ margin: '6px 0 0 0', fontSize: 26, color: '#14532d' }}>{occupancyReport.totalStoredDocuments}</h2>
                  </div>
                  <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: 16 }}>
                    <span style={{ fontSize: 11, fontWeight: 700, color: '#475569' }}>CAPACIDADE INSTALADA</span>
                    <h2 style={{ margin: '6px 0 0 0', fontSize: 26, color: '#0f172a' }}>{occupancyReport.totalCapacity} pastas</h2>
                  </div>
                  <div style={{ background: '#faf5ff', border: '1px solid #e9d5ff', borderRadius: 8, padding: 16 }}>
                    <span style={{ fontSize: 11, fontWeight: 700, color: '#6b21a8' }}>CAIXAS BOX ATIVAS</span>
                    <h2 style={{ margin: '6px 0 0 0', fontSize: 26, color: '#581c87' }}>{occupancyReport.totalBoxes}</h2>
                  </div>
                </div>

                <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
                  <button
                    onClick={() => {
                      exportCsv(
                        'Inventario_Arquivo_Fisico',
                        ['Caixa', 'Código Identificador', 'Capacidade', 'Itens Custodiados', 'Espaço Livre', '% Ocupação'],
                        occupancyReport.boxes.map((b) => [b.name, b.code || '-', b.capacity, b.stored, b.freeSpace, `${b.occupancyPercentage}%`])
                      );
                    }}
                    style={{
                      padding: '8px 14px',
                      borderRadius: 6,
                      background: '#16a34a',
                      color: 'white',
                      border: 'none',
                      fontWeight: 600,
                      fontSize: 12,
                      cursor: 'pointer',
                    }}
                  >
                    📊 Exportar Inventário Físico (CSV)
                  </button>
                </div>

                {/* Tabela de Caixas e Taxa de Ocupação */}
                <div style={{ overflowX: 'auto', background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8 }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                    <thead>
                      <tr style={{ background: '#f8fafc', borderBottom: '2px solid #cbd5e1' }}>
                        <th style={{ padding: '10px 14px', textAlign: 'left' }}>Caixa Box</th>
                        <th style={{ padding: '10px 14px', textAlign: 'left' }}>Código / QR</th>
                        <th style={{ padding: '10px 14px', textAlign: 'left' }}>Capacidade</th>
                        <th style={{ padding: '10px 14px', textAlign: 'left' }}>Ocupados</th>
                        <th style={{ padding: '10px 14px', textAlign: 'left' }}>Espaço Livre</th>
                        <th style={{ padding: '10px 14px', textAlign: 'left', width: 220 }}>% de Ocupação</th>
                      </tr>
                    </thead>
                    <tbody>
                      {occupancyReport.boxes.map((b) => (
                        <tr key={b.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                          <td style={{ padding: '10px 14px', fontWeight: 600 }}>{b.name}</td>
                          <td style={{ padding: '10px 14px', color: '#64748b' }}>{b.code || '-'}</td>
                          <td style={{ padding: '10px 14px' }}>{b.capacity}</td>
                          <td style={{ padding: '10px 14px', fontWeight: 600 }}>{b.stored}</td>
                          <td style={{ padding: '10px 14px' }}>{b.freeSpace}</td>
                          <td style={{ padding: '10px 14px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                              <div style={{ flex: 1, height: 8, background: '#e2e8f0', borderRadius: 4, overflow: 'hidden' }}>
                                <div
                                  style={{
                                    width: `${b.occupancyPercentage}%`,
                                    height: '100%',
                                    background: b.occupancyPercentage >= 90 ? '#ef4444' : b.occupancyPercentage >= 70 ? '#f59e0b' : '#22c55e',
                                  }}
                                />
                              </div>
                              <span style={{ fontSize: 11, fontWeight: 700, width: 35 }}>{b.occupancyPercentage}%</span>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : null}
          </div>
        )}

        {/* ABA 4: Custódia & Empréstimos */}
        {activeTab === 'movements' && (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 10 }}>
              <div style={{ display: 'flex', gap: 8 }}>
                {['all', 'pending', 'overdue', 'returned'].map((st) => (
                  <button
                    key={st}
                    onClick={() => {
                      setMovementFilterStatus(st);
                      loadMovementsReport(st);
                    }}
                    style={{
                      padding: '6px 14px',
                      borderRadius: 16,
                      border: movementFilterStatus === st ? 'none' : '1px solid #cbd5e1',
                      background: movementFilterStatus === st ? theme.colors.primary : '#fff',
                      color: movementFilterStatus === st ? '#fff' : '#475569',
                      fontSize: 12,
                      fontWeight: 600,
                      cursor: 'pointer',
                    }}
                  >
                    {st === 'all' && 'Todos'}
                    {st === 'pending' && `Pendentes (${movementsReport?.pendingCount || 0})`}
                    {st === 'overdue' && `⚠️ Em Atraso (${movementsReport?.overdueCount || 0})`}
                    {st === 'returned' && 'Devolvidos'}
                  </button>
                ))}
              </div>

              {movementsReport && (
                <button
                  onClick={() => {
                    exportCsv(
                      'Relatorio_Emprestimos_Custodia',
                      ['Tipo', 'Status', 'Documento', 'Mutuário', 'Documento Mutuário', 'Responsável', 'Data Saída', 'Previsão Retorno'],
                      movementsReport.movements.map((m) => [
                        m.type,
                        m.status,
                        m.documentName,
                        m.borrowerName,
                        m.borrowerDocument || '-',
                        m.responsibleUserName,
                        m.departureDate ? format(new Date(m.departureDate), 'dd/MM/yyyy') : '-',
                        m.expectedReturnDate ? format(new Date(m.expectedReturnDate), 'dd/MM/yyyy') : '-',
                      ])
                    );
                  }}
                  style={{
                    padding: '8px 14px',
                    borderRadius: 6,
                    background: '#16a34a',
                    color: 'white',
                    border: 'none',
                    fontWeight: 600,
                    fontSize: 12,
                    cursor: 'pointer',
                  }}
                >
                  📊 Exportar Movimentações (CSV)
                </button>
              )}
            </div>

            {loadingMovements ? (
              <LoaderComponent />
            ) : movementsReport ? (
              <div style={{ overflowX: 'auto', background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8 }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                  <thead>
                    <tr style={{ background: '#f8fafc', borderBottom: '2px solid #cbd5e1' }}>
                      <th style={{ padding: '10px 14px', textAlign: 'left' }}>Status</th>
                      <th style={{ padding: '10px 14px', textAlign: 'left' }}>Documento / Item</th>
                      <th style={{ padding: '10px 14px', textAlign: 'left' }}>Mutuário (Solicitante)</th>
                      <th style={{ padding: '10px 14px', textAlign: 'left' }}>Operador Responsável</th>
                      <th style={{ padding: '10px 14px', textAlign: 'left' }}>Data de Saída</th>
                      <th style={{ padding: '10px 14px', textAlign: 'left' }}>Previsão de Retorno</th>
                    </tr>
                  </thead>
                  <tbody>
                    {movementsReport.movements.map((m) => (
                      <tr key={m.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                        <td style={{ padding: '10px 14px' }}>
                          <span
                            style={{
                              fontSize: 11,
                              fontWeight: 700,
                              padding: '2px 8px',
                              borderRadius: 12,
                              background: m.isOverdue ? '#fee2e2' : m.status === 'pending' ? '#fef3c7' : '#dcfce7',
                              color: m.isOverdue ? '#b91c1c' : m.status === 'pending' ? '#b45309' : '#15803d',
                            }}
                          >
                            {m.isOverdue ? 'Atrasado' : m.status === 'pending' ? 'Empréstimo Ativo' : 'Devolvido'}
                          </span>
                        </td>
                        <td style={{ padding: '10px 14px', fontWeight: 600 }}>{m.documentName}</td>
                        <td style={{ padding: '10px 14px' }}>
                          <div>{m.borrowerName}</div>
                          {m.borrowerDocument && <span style={{ fontSize: 10, color: '#94a3b8' }}>CPF: {m.borrowerDocument}</span>}
                        </td>
                        <td style={{ padding: '10px 14px', color: '#64748b' }}>{m.responsibleUserName}</td>
                        <td style={{ padding: '10px 14px' }}>{m.departureDate ? format(new Date(m.departureDate), 'dd/MM/yyyy') : '-'}</td>
                        <td style={{ padding: '10px 14px', fontWeight: m.isOverdue ? 700 : 400, color: m.isOverdue ? '#dc2626' : 'inherit' }}>
                          {m.expectedReturnDate ? format(new Date(m.expectedReturnDate), 'dd/MM/yyyy') : '-'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : null}
          </div>
        )}

        {/* ABA 5: Auditoria & LGPD */}
        {activeTab === 'lgpd' && (
          <div>
            {loadingLgpd ? (
              <LoaderComponent />
            ) : lgpdReport ? (
              <div>
                {/* Métricas de Assinaturas & Auditoria */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16, marginBottom: 20 }}>
                  <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 8, padding: 16 }}>
                    <span style={{ fontSize: 11, fontWeight: 700, color: '#166534' }}>DOCUMENTOS ASSINADOS</span>
                    <h2 style={{ margin: '6px 0 0 0', fontSize: 26, color: '#14532d' }}>{lgpdReport.summary.totalSignedDocuments}</h2>
                  </div>
                  <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 8, padding: 16 }}>
                    <span style={{ fontSize: 11, fontWeight: 700, color: '#1e40af' }}>ASSINATURAS GOV.BR</span>
                    <h2 style={{ margin: '6px 0 0 0', fontSize: 26, color: '#1e3a8a' }}>{lgpdReport.summary.totalGovBrSignatures}</h2>
                  </div>
                  <div style={{ background: '#fdf4ff', border: '1px solid #f0abfc', borderRadius: 8, padding: 16 }}>
                    <span style={{ fontSize: 11, fontWeight: 700, color: '#86198f' }}>ASSINATURAS ICP-BRASIL</span>
                    <h2 style={{ margin: '6px 0 0 0', fontSize: 26, color: '#701a75' }}>{lgpdReport.summary.totalIcpSignatures}</h2>
                  </div>
                  <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: 16 }}>
                    <span style={{ fontSize: 11, fontWeight: 700, color: '#475569' }}>TRILHA DE AUDITORIA TOTAL</span>
                    <h2 style={{ margin: '6px 0 0 0', fontSize: 26, color: '#0f172a' }}>{lgpdReport.summary.totalAuditEvents}</h2>
                  </div>
                </div>

                <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
                  <button
                    onClick={() => {
                      exportCsv(
                        'Auditoria_LGPD_Eventos',
                        ['Ação', 'Usuário', 'E-mail', 'Documento', 'Data e Hora'],
                        lgpdReport.recentEvents.map((e) => [
                          e.action,
                          e.userName,
                          e.userEmail,
                          e.documentName,
                          e.createdAt ? format(new Date(e.createdAt), 'dd/MM/yyyy HH:mm:ss') : '-',
                        ])
                      );
                    }}
                    style={{
                      padding: '8px 14px',
                      borderRadius: 6,
                      background: '#16a34a',
                      color: 'white',
                      border: 'none',
                      fontWeight: 600,
                      fontSize: 12,
                      cursor: 'pointer',
                    }}
                  >
                    📊 Exportar Log LGPD (CSV)
                  </button>
                </div>

                {/* Tabela de Eventos de Auditoria Recentes */}
                <div style={{ overflowX: 'auto', background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8 }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                    <thead>
                      <tr style={{ background: '#f8fafc', borderBottom: '2px solid #cbd5e1' }}>
                        <th style={{ padding: '10px 14px', textAlign: 'left' }}>Ação Registrada</th>
                        <th style={{ padding: '10px 14px', textAlign: 'left' }}>Usuário / Operador</th>
                        <th style={{ padding: '10px 14px', textAlign: 'left' }}>Documento</th>
                        <th style={{ padding: '10px 14px', textAlign: 'left' }}>Data e Hora</th>
                      </tr>
                    </thead>
                    <tbody>
                      {lgpdReport.recentEvents.map((ev) => (
                        <tr key={ev.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                          <td style={{ padding: '10px 14px', fontWeight: 600, color: theme.colors.primary }}>
                            {ev.action}
                          </td>
                          <td style={{ padding: '10px 14px' }}>
                            <div>{ev.userName}</div>
                            {ev.userEmail && <span style={{ fontSize: 10, color: '#94a3b8' }}>{ev.userEmail}</span>}
                          </td>
                          <td style={{ padding: '10px 14px' }}>{ev.documentName}</td>
                          <td style={{ padding: '10px 14px', color: '#64748b' }}>
                            {ev.createdAt ? format(new Date(ev.createdAt), 'dd/MM/yyyy HH:mm:ss') : '-'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : null}
          </div>
        )}
      </DashStyles.LeftPanel>

      {activeTab === 'general' && (
        <DashStyles.RightPanel hidden={mobile}>
          <GeneralFilters handleGetStatistics={handleGetStatistics} />
        </DashStyles.RightPanel>
      )}
    </Container>
  );
};

export default Reports;
